import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
    Box, Typography, Button, Snackbar, Dialog,
    DialogActions, DialogContent, DialogContentText, DialogTitle,
    Paper, Alert, CircularProgress
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../store";
import { setCurrentPage } from "../../store/auth/authReducer";
import { logOut } from "../../utils/logOut";
import TranslatedText from "../../utils/Translate";

export default function ApiKey() {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const language = useSelector((state: any) => state.auth.authData.language);

    const [apiKey, setApiKey] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    useEffect(() => {
        dispatch(setCurrentPage("API Key"));
        fetchApiKey();
    }, [dispatch]);

    const fetchApiKey = async () => {
        setLoading(true);
        try {
            const res = await axios.get("https://cardgarantsales.com/restapi/apikey", {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("access_token"),
                },
            });
            setApiKey(res.data[0]?.api_key || null);
        } catch (err: any) {
            if (err.response?.status === 401) logOut(dispatch);
        } finally {
            setLoading(false);
        }
    };

    const generateApiKey = async () => {
        try {
            const res = await axios.post("https://cardgarantsales.com/restapi/apikey", {}, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("access_token"),
                },
            });
            fetchApiKey();
            setConfirmDialogOpen(false);
        } catch (err: any) {
            if (err.response?.status === 401) logOut(dispatch);
        }
    };

    const handleCopy = () => {
        if (apiKey) {
            navigator.clipboard.writeText(apiKey);
            setSnackbarOpen(true);
        }
    };

    return (
        <Box sx={{ display: "flex", justifyContent: "center", p: { xs: 2, sm: 4 }, alignItems: "center", minHeight: "80vh" }}>
            <Paper elevation={3} sx={{ p: 4, width: "100%", maxWidth: 700, borderRadius: 2, minWidth: 300 }}>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                    <TranslatedText text="Your API key" />
                </Typography>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : apiKey ? (
                    <>
                        <Box
                            sx={{
                                p: 2,
                                backgroundColor: "#f9f9f9",
                                border: "1px solid #ddd",
                                borderRadius: 2,
                                mb: 3,
                                fontFamily: "monospace",
                                fontSize: 16,
                                wordBreak: "break-all",
                            }}
                        >
                            {apiKey}
                        </Box>

                        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ContentCopyIcon />}
                                onClick={handleCopy}
                                fullWidth
                            >
                                <TranslatedText text="Copy" />
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<RefreshIcon />}
                                onClick={() => setConfirmDialogOpen(true)}
                                fullWidth
                            >
                                <TranslatedText text="Recreate" />
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={generateApiKey}
                        fullWidth
                        sx={{ mt: 2 }}
                    >
                        <TranslatedText text="Create API key" />
                    </Button>
                )}

                <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 2, fontSize: 14 }}>
                    <TranslatedText text="Do not share your API key with others. Do not expose it in the browser or client-side code. Store your key in a secure location." />
                </Alert>

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={() => setSnackbarOpen(false)}
                    message={<TranslatedText text="Key copied!" />}
                />

                <Dialog
                    open={confirmDialogOpen}
                    onClose={() => setConfirmDialogOpen(false)}
                >
                    <DialogTitle>
                        <TranslatedText text="Confirmation" />
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            <TranslatedText text="Are you sure you want to recreate the API key? The old key will become invalid." />
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDialogOpen(false)} color="inherit">
                            <TranslatedText text="Cancel" />
                        </Button>
                        <Button onClick={generateApiKey} color="error" variant="contained">
                            <TranslatedText text="Recreate" />
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Box>
    );
}
